import {
  AppUiRecordDataService,
  AppUiSettled,
} from '@omni/data-services/app-ui/app-ui-record.data.service';
import {
  NotificationService,
  ToastStyle,
} from '@omni/services/notification/notification.service';
import {
  ORDER_SCREEN_ENTITY,
  OrderCaptureDataService,
} from '@omni/data-services/order-capture/order-capture.data.service';
import { DeviceService } from '@omni/services/device/device.service';
import { Injectable } from '@angular/core';
import { MyOrdersService } from './order-list/my-orders.service';
import { TranslateService } from '@ngx-translate/core';
import { deviceOrderId } from './order-list/order-list';
import { filter } from 'rxjs';
import { orderMessage } from './order-messages';

/**
 * A submit or scrap sent at a later sync was refused, after the rep was told it went through: say so
 * then, wherever the rep is. A send a screen waited on is its to tell.
 */
export function isLateOrderRefusal(settled: AppUiSettled): boolean {
  return (
    !settled.reported &&
    settled.status === 'refused' &&
    settled.record.entity === ORDER_SCREEN_ENTITY &&
    (settled.mode === 'submit' || settled.mode === 'scrap')
  );
}

/** An order deleted offline, its delete sent at a sync and done. */
export function scrappedOrder(settled: AppUiSettled): boolean {
  return (
    settled.status === 'landed' &&
    settled.mode === 'scrap' &&
    settled.record.entity === ORDER_SCREEN_ENTITY
  );
}

/** An order saved or submitted at a sync: it has a number now, which the device may not hold yet. */
export function landedOrder(settled: AppUiSettled): boolean {
  return (
    settled.status === 'landed' &&
    settled.mode !== 'scrap' &&
    settled.record.entity === ORDER_SCREEN_ENTITY
  );
}

@Injectable({ providedIn: 'root' })
export class OrderSyncNoticesService {
  private started = false;

  constructor(
    private readonly records: AppUiRecordDataService,
    private readonly notifications: NotificationService,
    private readonly translate: TranslateService,
    private readonly orders: OrderCaptureDataService,
    private readonly deviceService: DeviceService,
    private readonly myOrders: MyOrdersService,
  ) {}

  /** Once, from app start: the queue drains at every sync, not only while an order is open. */
  public start(): void {
    if (this.started) return;
    this.started = true;
    this.records.settled$
      .pipe(filter(isLateOrderRefusal))
      .subscribe((settled) =>
        this.notifications.notify(
          orderMessage(
            (key, params) => this.translate.instant(key, params) as string,
            settled.mode,
            settled.record.serverName,
            settled.reason ?? '',
          ),
          'order-sync-notices',
          'top',
          ToastStyle.DANGER,
          5000,
        ),
      );
    this.records.settled$
      .pipe(filter(landedOrder))
      .subscribe((settled) => void this.nameOrder(settled.record.localId));
    // Its record is gone now; the order list's synced copy would bring it back until the next sync.
    this.records.settled$
      .pipe(filter(scrappedOrder))
      .subscribe(
        (settled) => void this.myOrders.forget(deviceOrderId(settled.record)),
      );
  }

  /** Numbered as it lands, not only while its screen is open (that screen names it itself). */
  private async nameOrder(localId: string): Promise<void> {
    if (this.records.isOnScreen(localId) || this.deviceService.isOffline)
      return;
    const record = await this.records.get(localId).catch(() => null);
    if (!record?.serverId || record.serverName) return;
    const name = await this.orders
      .getOrderNumber(record.serverId)
      .catch(() => null);
    if (name)
      await this.records.setServerName(localId, name).catch(() => undefined);
  }
}
